"use client";

import Link from "next/link";
import { useId } from "react";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FilterCheckboxInput } from "@/features/search/components/filters/input/checkbox-input";
import { FilterSelectInput } from "@/features/search/components/filters/input/select-input";
import { NetworkMembersSelect } from "@/features/network/components/network-members-select";
import {
  NETWORK_FILTER_DEFAULT,
  NETWORK_SOURCES,
  type NetworkSource,
  toggleSource,
} from "@/features/network/lib/filter";
import { NETWORK_ROUTE } from "@/features/network/lib/routes";
import type {
  NetworkFilterSchema,
  NetworkFilterSchemaMinStrength,
  NetworkFilterSchemaMode,
} from "@/lib/api/model";
import { FilterFieldContainer, FilterFieldContent, FilterFieldLabel } from "../shared";

const SOURCE_LABELS: Record<NetworkSource, string> = {
  connections: "LinkedIn connections",
  overlaps: "Worked or studied together",
};

const STRENGTH_OPTIONS: { value: NetworkFilterSchemaMinStrength; label: string }[] = [
  { value: "low", label: "Any overlap" },
  { value: "medium", label: "Medium or strong" },
  { value: "high", label: "Strong only" },
];

type NetworkChoice = "anyone" | NetworkFilterSchemaMode;

const CHOICES: { value: NetworkChoice; label: string }[] = [
  { value: "anyone", label: "Anyone" },
  { value: "include", label: "Only people the team knows" },
  { value: "exclude", label: "Only people the team doesn't know" },
];

/** Active filter count: the network filter is one filter, however it's tuned. */
export function countNetworkFilter(value: NetworkFilterSchema | null | undefined): number {
  return value ? 1 : 0;
}

export function FilterNetworkSection() {
  const { filters, updateFilter } = useTalentSearchFilters();
  const id = useId();
  const network = filters.network ?? undefined;
  const sources = (network?.sources ?? NETWORK_SOURCES) as NetworkSource[];
  const memberIds = network?.member_ids ?? [];
  const choice: NetworkChoice = network ? (network.mode ?? "include") : "anyone";

  const update = (patch: Partial<NetworkFilterSchema>) =>
    updateFilter("network", { ...network, ...patch });

  const choose = (next: NetworkChoice) => {
    if (next === choice) return;
    if (next === "anyone") {
      updateFilter("network", undefined);
      return;
    }
    // Switching between include and exclude keeps the tuning; `include` is
    // the backend default, so it stays implicit.
    const value: NetworkFilterSchema = {
      ...(network ?? {
        sources: [...NETWORK_FILTER_DEFAULT.sources],
        member_ids: [],
        min_strength: NETWORK_FILTER_DEFAULT.min_strength,
      }),
    };
    delete value.mode;
    if (next === "exclude") value.mode = "exclude";
    updateFilter("network", value);
  };

  return (
    <>
      <RadioGroup
        aria-label="Team network"
        value={choice}
        onValueChange={(value) => choose(value as NetworkChoice)}
        className="flex flex-col gap-1.5"
      >
        {CHOICES.map((c) => (
          <label
            key={c.value}
            htmlFor={`${id}-${c.value}`}
            className="flex cursor-pointer items-center gap-2 text-sm text-heading"
          >
            <RadioGroupItem id={`${id}-${c.value}`} value={c.value} />
            {c.label}
          </label>
        ))}
      </RadioGroup>
      {network ? (
        <>
          <FilterFieldContainer>
            <FilterFieldLabel>Through</FilterFieldLabel>
            <FilterFieldContent className="flex flex-col gap-1.5">
              {NETWORK_SOURCES.map((source) => (
                <FilterCheckboxInput
                  key={source}
                  checked={sources.includes(source)}
                  onChange={(on) => update({ sources: toggleSource(sources, source, on) })}
                >
                  {SOURCE_LABELS[source]}
                </FilterCheckboxInput>
              ))}
            </FilterFieldContent>
          </FilterFieldContainer>
          {sources.includes("overlaps") ? (
            <FilterFieldContainer>
              <FilterFieldLabel>Overlap strength</FilterFieldLabel>
              <FilterFieldContent>
                <FilterSelectInput
                  options={STRENGTH_OPTIONS}
                  value={network.min_strength ?? NETWORK_FILTER_DEFAULT.min_strength}
                  onChange={(min_strength) => update({ min_strength })}
                />
              </FilterFieldContent>
            </FilterFieldContainer>
          ) : null}
          <FilterFieldContainer>
            <FilterFieldLabel>Teammates</FilterFieldLabel>
            <FilterFieldContent>
              <NetworkMembersSelect
                value={memberIds}
                onChange={(member_ids) => update({ member_ids })}
              />
            </FilterFieldContent>
          </FilterFieldContainer>
          <p className="text-xs text-contrast/50">
            Connections come from uploaded LinkedIn exports; overlaps from
            teammates&apos; own profiles. Teammates include people added who
            aren&apos;t in Arbi.{" "}
            <Link className="underline" href={NETWORK_ROUTE}>
              Team network
            </Link>
          </p>
        </>
      ) : null}
    </>
  );
}
